/**
 * Drawn icons, one stroke system.
 *
 * Replaces the Unicode glyphs the UI used as icons — `×` for close, `+` for
 * add, `✓` for selected. A glyph is typeset by whatever font resolves it, so it
 * arrives at a weight, a width and an optical size that belong to no design
 * system, next to buttons that were drawn. `×` in particular renders as a
 * mathematical multiplication sign at text weight, thinner and smaller than the
 * frame around it.
 *
 * All three are drawn on the same 24-unit grid at the stroke weight
 * `music-toggle.ts` already established, so a close button and a mute button
 * sitting in the same header read as one family.
 *
 * `currentColor` throughout: an icon inherits its button's colour and every
 * state that button already defines, rather than needing its own.
 */
const PATHS = {
  /** Close, dismiss, cancel. */
  close: 'M6 6l12 12M18 6L6 18',
  /** Add, create, new. */
  add: 'M12 5v14M5 12h14',
  /** Chosen, confirmed, already yours. */
  check: 'M5 12.5l4.5 4.5L19 7.5',
  /** Time left — the lobby offer's countdown. */
  clock: 'M12 3.5a8.5 8.5 0 1 0 0 17a8.5 8.5 0 1 0 0-17M12 7.5V12l3 2',
} as const;

export type IconName = keyof typeof PATHS;

/**
 * Inline SVG markup for one icon.
 *
 * `aria-hidden` because every call site here sits inside a control that already
 * carries its own accessible name — an `aria-label` on an icon-only button, or
 * visible text beside it. An icon that also announced itself would be read
 * twice.
 */
export function iconMarkup(name: IconName, size = 18): string {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none"
    stroke="currentColor" stroke-width="1.7" stroke-linecap="round"
    stroke-linejoin="round" aria-hidden="true" focusable="false"
  ><path d="${PATHS[name]}" /></svg>`;
}


/**
 * Swap an `<img>` to its `data-fallback` when the real file is not there.
 *
 * Added for the 7-tier talent expansion (2026-09-04), which shipped 52 nodes
 * ahead of their art. A 404 renders as the browser's broken-image glyph, which
 * reads as a bug in the game rather than as a picture that has not arrived
 * yet — and the tree is the one screen a player studies rather than glances at.
 *
 * On `error` rather than from a manifest of what exists: the day real art
 * ships for a node, the error stops firing and nothing here has to be told.
 *
 * A cached 404 can complete *before* this runs — `complete` true,
 * `naturalWidth` 0 — and a listener attached afterwards never fires for it.
 * Checking both is the difference between a fallback that works on first load
 * and one that works on every load except the ones a returning player sees.
 */
export function wireIconFallbacks(root: ParentNode): void {
  root.querySelectorAll<HTMLImageElement>('img[data-fallback]').forEach((img) => {
    const swap = (): void => {
      const fallback = img.dataset.fallback;
      // Cleared first, so a fallback that is itself missing cannot loop.
      delete img.dataset.fallback;
      if (fallback) img.src = fallback;
    };
    if (img.complete && img.naturalWidth === 0) swap();
    else img.addEventListener('error', swap, { once: true });
  });
}
